'use client';

import { useState } from 'react';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { IoPlayOutline } from 'react-icons/io5';

interface VideoPlayerProps {
  videoUrl: string;
  thumbnailUrl: string;
}

export function VideoPlayer({ videoUrl, thumbnailUrl }: VideoPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);

  // Extract YouTube video ID
  const getVideoId = (url: string) => {
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('youtu.be')) {
        return urlObj.pathname.split('/')[1];
      }
      return urlObj.searchParams.get('v') || 'dp4OYQLzZ2o';
    } catch {
      return 'dp4OYQLzZ2o'; // Fallback video ID
    }
  };

  const videoId = getVideoId(videoUrl);
  const embedUrl = `https://www.youtube.com/embed/${videoId}${
    isPlaying ? '?autoplay=1&mute=1&rel=0' : ''
  }`;

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-[16px] sm:rounded-[24px]">
      {/* Thumbnail Overlay */}
      {!isPlaying && (
        <button
          type="button"
          className="relative block h-full w-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
          onClick={handlePlay}
          aria-label="Play video"
        >
          <Image
            src={thumbnailUrl}
            alt="Video thumbnail"
            width={0}
            height={0}
            sizes="100vw"
            className={cn(
              'absolute left-0 top-0 h-full w-full object-cover object-center',
              'cursor-pointer transition-opacity duration-300',
            )}
            loading="lazy"
          />
          {/* Play Icon */}
          <div className="absolute left-1/2 top-1/2 z-10 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-primary sm:h-16 sm:w-16">
            <IoPlayOutline size={24} className="translate-x-0.5" />
          </div>
        </button>
      )}
      {/* Iframe */}
      {isPlaying && <iframe
        width="100%"
        height="100%"
        src={embedUrl}
        title="YouTube video player"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className={cn(
          'absolute left-0 top-0 h-full w-full',
          isPlaying ? 'opacity-100' : 'opacity-0', // Hide iframe visually when not playing
        )}
      />}
    </div>
  );
}
